import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { 
  Shield, ArrowLeft, Plus, Save, Trash2, Edit3, X,
  AlertTriangle, CheckCircle2, Clock, Phone,
  ExternalLink, AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

// ==========================================
// LISTA DE GERENCIADORAS
// ==========================================
const GERENCIADORAS = [
  'Buonny',
  'Pamcary',
  'Opentech',
  'BRK Tecnologia',
  'Tecnorisk',
  'NOX Gerenciadora de Risco',
  'Consultebras',
  'Golden Risk',
  'GVRisk'
];

const STATUS_CONFIG = {
  ativo: { cor: 'bg-green-100 text-green-700 border-green-200', icone: CheckCircle2, label: 'Ativo' },
  pendente: { cor: 'bg-yellow-100 text-yellow-700 border-yellow-200', icone: Clock, label: 'Pendente' },
  vencido: { cor: 'bg-red-100 text-red-700 border-red-200', icone: AlertTriangle, label: 'Vencido' },
  sem_cadastro: { cor: 'bg-slate-100 text-slate-500 border-slate-200', icone: X, label: 'Sem Cadastro' }
};

export default function GerenciadoraRisco() {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  const [gerenciadoras, setGerenciadoras] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [motoristaId, setMotoristaId] = useState(null);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGerenciadora, setEditingGerenciadora] = useState(null);
  
  const [formData, setFormData] = useState({
    gerenciadora: '',
    status: 'sem_cadastro',
    data_cadastro: '',
    data_vencimento: '',
    observacao: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Carregar dados
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.id) {
      carregarDados();
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const carregarDados = async () => {
    setIsLoading(true);
    try {
      // Buscar motorista_id pelo usuario_id
      const { data: motorista, error: errMotorista } = await supabase
        .from('motoristas')
        .select('id')
        .eq('usuario_id', user.id)
        .single();

      if (errMotorista) {
        console.error('Erro ao buscar motorista:', errMotorista);
        toast.error('Erro ao identificar motorista');
        setIsLoading(false);
        return;
      }

      if (motorista) {
        setMotoristaId(motorista.id);

        // Buscar gerenciadoras do motorista
        const { data: lista, error: errGerenciadoras } = await supabase
          .from('gerenciadoras_risco')
          .select('*')
          .eq('motorista_id', motorista.id)
          .order('created_at', { ascending: false });

        if (errGerenciadoras) {
          console.error('Erro ao buscar gerenciadoras:', errGerenciadoras);
          toast.error('Erro ao carregar gerenciadoras');
        } else {
          setGerenciadoras(lista || []);
        }
      }
    } catch (err) {
      console.error('Erro:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      gerenciadora: '',
      status: 'sem_cadastro',
      data_cadastro: '',
      data_vencimento: '',
      observacao: ''
    });
    setEditingGerenciadora(null);
    setDialogOpen(false);
  };

  const handleEdit = (item) => {
    setEditingGerenciadora(item);
    setFormData({
      gerenciadora: item.gerenciadora,
      status: item.status,
      data_cadastro: item.data_cadastro || '',
      data_vencimento: item.data_vencimento || '',
      observacao: item.observacao || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.gerenciadora) {
      toast.error('Selecione a gerenciadora');
      return;
    }

    setIsSubmitting(true);

    try {
      const dadosSalvar = {
        motorista_id: motoristaId,
        gerenciadora: formData.gerenciadora,
        status: formData.status,
        data_cadastro: formData.data_cadastro || null,
        data_vencimento: formData.data_vencimento || null,
        observacao: formData.observacao || null
      };

      if (editingGerenciadora) {
        // UPDATE
        const { error } = await supabase
          .from('gerenciadoras_risco')
          .update(dadosSalvar)
          .eq('id', editingGerenciadora.id);

        if (error) throw error;
        toast.success('Gerenciadora atualizada!');
      } else {
        // INSERT
        const { error } = await supabase
          .from('gerenciadoras_risco')
          .insert([dadosSalvar]);

        if (error) throw error;
        toast.success('Gerenciadora cadastrada!');
      }

      resetForm();
      carregarDados();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      toast.error('Erro ao salvar: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja remover esta gerenciadora?')) return;

    try {
      const { error } = await supabase
        .from('gerenciadoras_risco')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Gerenciadora removida');
      carregarDados();
    } catch (err) {
      console.error('Erro ao excluir:', err);
      toast.error('Erro ao remover: ' + err.message);
    }
  };

  // Verifica se precisa de atenção (vencido ou sem cadastro)
  const precisaAtencao = gerenciadoras.some(g => 
    g.status === 'vencido' || g.status === 'sem_cadastro'
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/perfil')}>
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl text-slate-800">Gerenciadora de Risco</span>
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => resetForm()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova
                </Button>
              </DialogTrigger>
              
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingGerenciadora ? 'Editar Cadastro' : 'Nova Gerenciadora'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Gerenciadora */}
                  <div className="space-y-2">
                    <Label>Gerenciadora *</Label>
                    <Select 
                      value={formData.gerenciadora} 
                      onValueChange={(v) => setFormData({...formData, gerenciadora: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {GERENCIADORAS.map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <Label>Status *</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(v) => setFormData({...formData, status: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="pendente">Pendente</SelectItem>
                        <SelectItem value="vencido">Vencido</SelectItem>
                        <SelectItem value="sem_cadastro">Sem Cadastro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Data Cadastro</Label>
                      <Input 
                        type="date"
                        value={formData.data_cadastro}
                        onChange={(e) => setFormData({...formData, data_cadastro: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data Vencimento</Label>
                      <Input 
                        type="date"
                        value={formData.data_vencimento}
                        onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Observação - sem placeholder pré-definido */}
                  <div className="space-y-2">
                    <Label>Observação</Label>
                    <Input 
                      value={formData.observacao}
                      onChange={(e) => setFormData({...formData, observacao: e.target.value})}
                      placeholder="Digite sua observação ou dúvida aqui..."
                    />
                  </div>

                  {/* Botões */}
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setDialogOpen(false)}
                    >
                      <X className="w-4 h-4 mr-2" /> Cancelar
                    </Button>
                    <Button 
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Salvando...' : <><Save className="w-4 h-4 mr-2" /> {editingGerenciadora ? 'Salvar' : 'Cadastrar'}</>}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* ✅ BANNER SEM IMAGEM - Só texto com fundo gradiente */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">
              Proteção e Segurança para sua Carga
            </h2>
            <p className="text-blue-200 text-sm">
              Cadastre suas gerenciadoras de risco e mantenha sua operação sempre protegida.
            </p>
          </div>
        </div>
      </div>

      {/* Alerta se precisa de atenção */}
      {precisaAtencao && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Atenção!</p>
              <p className="text-sm text-yellow-700">
                Você possui gerenciadoras com status <strong>vencido</strong> ou <strong>sem cadastro</strong>. 
                Atualize seu cadastro para continuar transportando com segurança.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : gerenciadoras.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="p-8 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                Nenhuma gerenciadora cadastrada
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Cadastre suas gerenciadoras de risco para manter sua segurança atualizada
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Cadastrar Gerenciadora
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {gerenciadoras.map((item) => {
              const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.sem_cadastro;
              const IconeStatus = config.icone;
              
              return (
                <Card key={item.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <Shield className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900">{item.gerenciadora}</h3>
                            <Badge className={config.cor}>
                              <IconeStatus className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                          </div>
                          
                          <div className="flex gap-4 mt-1 text-xs text-slate-400">
                            {item.data_cadastro && (
                              <span>Cadastro: {new Date(item.data_cadastro).toLocaleDateString('pt-BR')}</span>
                            )}
                            {item.data_vencimento && (
                              <span className={item.status === 'vencido' ? 'text-red-500 font-medium' : ''}>
                                Vencimento: {new Date(item.data_vencimento).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                          
                          {item.observacao && (
                            <p className="text-sm text-slate-500 mt-2 italic">
                              "{item.observacao}"
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(item)}
                        >
                          <Edit3 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Botão de ajuda */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Precisa de ajuda?</p>
              <p className="text-sm text-blue-700 mb-3">
                Se você não tem cadastro em nenhuma gerenciadora, nossa equipe pode ajudar!
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-blue-300 text-blue-700"
                  onClick={() => window.open('https://wa.me/5565981181997?text=Olá! Preciso cadastrar uma gerenciadora de risco.', '_blank')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Falar com Admin
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-blue-300 text-blue-700"
                  onClick={() => window.open('https://wa.me/5565981181997?text=Olá! Quero fazer um novo cadastro de gerenciadora de risco.', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Novo Cadastro
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}